/**
 * category-picks.js
 * ---------------------------------------------------------------
 * Web component <category-picks> usato dallo snippet
 * category-picks.liquid nella pagina prodotto.
 *
 * Cosa fa:
 * 1. Se lo snippet ha gia' renderizzato dei prodotti (mappa curata
 *    tramite il metafield sulla collezione), li lascia cosi' come
 *    sono e si limita a gestire il click sul pulsante "Aggiungi".
 * 2. Altrimenti chiede a Shopify quali prodotti potrebbero essere
 *    utili INSIEME al prodotto della pagina corrente: prova prima i
 *    consigli "complementari" e, se non ce ne sono ancora, usa i
 *    "correlati" come ripiego.
 * 3. In entrambi i casi, il pulsante "Aggiungi" aggiunge il prodotto
 *    al carrello via AJAX e apre il drawer, senza ricaricare la pagina.
 *
 * Usa solo le API pubbliche di Shopify (/recommendations/products.json
 * e /cart/add.js), quindi resta valida anche se il tema viene aggiornato.
 */

if (!customElements.get('category-picks')) {
  class CategoryPicks extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.category-picks__track');
      this.productId = this.dataset.productId;
      this.limit = this.dataset.limit || 5;
      this.currency = this.dataset.currency || 'EUR';
      this.locale = this.dataset.locale || 'it-IT';

      if (!this.productId || !this.track) return;

      this.addEventListener('click', (event) => {
        const button = event.target.closest('.category-picks__add-button');
        if (!button) return;
        this.addToCart(button.dataset.variantId, button);
      });

      if (this.track.children.length > 0) {
        // Il negoziante ha gia' definito a mano i prodotti complementari
        // per questa categoria (metafield sulla collezione): sono gia'
        // renderizzati dal server in category-picks.liquid, non li
        // sovrascriviamo con una chiamata a Shopify. Il click sul
        // pulsante "Aggiungi" e' gestito comunque dal listener sopra.
        return;
      }

      this.loadRecommendations();
    }

    async fetchRecommendations(intent) {
      const url = `/recommendations/products.json?product_id=${this.productId}&limit=${this.limit}&intent=${intent}`;
      const response = await fetch(url);
      if (!response.ok) throw new Error('Recommendations request failed');
      const data = await response.json();
      return (data.products || []).filter((product) => product.available);
    }

    async loadRecommendations() {
      this.track.innerHTML = '<div class="cart-upsell__loading" aria-hidden="true"></div>';

      try {
        let products = await this.fetchRecommendations('complementary');
        if (products.length === 0) {
          products = await this.fetchRecommendations('related');
        }

        if (products.length === 0) {
          this.remove();
          return;
        }

        this.renderProducts(products);
      } catch (error) {
        console.warn('category-picks: impossibile caricare i consigli prodotto', error);
        this.remove();
      }
    }

    formatMoney(amount) {
      // L'endpoint /recommendations/products.json restituisce i prezzi in
      // centesimi (come l'Ajax Cart API), non in euro decimali.
      const cents = typeof amount === 'string' ? parseFloat(amount) : amount;
      const value = cents / 100;
      try {
        return new Intl.NumberFormat(this.locale, {
          style: 'currency',
          currency: this.currency,
        }).format(value);
      } catch (error) {
        return `${value.toFixed(2)} ${this.currency}`;
      }
    }

    renderProducts(products) {
      this.track.innerHTML = '';
      products.forEach((product) => {
        this.track.appendChild(this.buildCard(product));
      });
    }

    buildCard(product) {
      const card = document.createElement('div');
      card.className = 'cart-upsell__card';

      const image = product.featured_image || (product.images && product.images[0]) || null;
      const firstAvailable = product.variants.find((variant) => variant.available) || product.variants[0];
      const priceHtml = this.buildPriceHtml(firstAvailable);

      card.innerHTML = `
        <a href="${product.url}" class="cart-upsell__image-link" tabindex="-1">
          ${
            image
              ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" loading="lazy" width="120" height="120" class="cart-upsell__image">`
              : ''
          }
        </a>
        <a href="${product.url}" class="cart-upsell__title">${this.escapeHtml(product.title)}</a>
        <div class="cart-upsell__price-row">${priceHtml}</div>
        <button
          type="button"
          class="cart-upsell__add-button category-picks__add-button button button--secondary"
          data-variant-id="${firstAvailable ? firstAvailable.id : ''}"
        >
          <span class="cart-upsell__add-label">Aggiungi</span>
        </button>
      `;

      return card;
    }

    buildPriceHtml(variant) {
      if (!variant) return '';
      const price = this.formatMoney(variant.price);
      const compareAtPrice = variant.compare_at_price;
      const onSale = compareAtPrice && parseFloat(compareAtPrice) > parseFloat(variant.price);

      if (onSale) {
        return `
          <s class="cart-upsell__compare-price">${this.formatMoney(compareAtPrice)}</s>
          <span class="cart-upsell__price cart-upsell__price--sale">${price}</span>
        `;
      }

      return `<span class="cart-upsell__price">${price}</span>`;
    }

    async addToCart(variantId, button) {
      if (!variantId) return;
      button.classList.add('is-loading');
      button.disabled = true;

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({
            items: [{ id: variantId, quantity: 1 }],
            sections: 'cart-drawer,cart-icon-bubble',
            sections_url: window.location.pathname,
          }),
        });

        const result = await response.json();

        if (!response.ok) {
          throw new Error(result.description || 'Impossibile aggiungere il prodotto al carrello');
        }

        this.refreshCartSections(result.sections);
        this.openCartDrawer();
      } catch (error) {
        console.warn('category-picks: errore durante l\'aggiunta al carrello', error);
        button.classList.add('has-error');
        setTimeout(() => button.classList.remove('has-error'), 2000);
      } finally {
        button.classList.remove('is-loading');
        button.disabled = false;
      }
    }

    refreshCartSections(sections) {
      if (!sections) return;

      if (sections['cart-drawer']) {
        const newDoc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        const newDrawer = newDoc.querySelector('#CartDrawer');
        const currentDrawer = document.getElementById('CartDrawer');
        if (newDrawer && currentDrawer) {
          currentDrawer.innerHTML = newDrawer.innerHTML;
        }

        const cartDrawerEl = document.querySelector('cart-drawer');
        const newCartDrawerEl = newDoc.querySelector('cart-drawer');
        if (cartDrawerEl && newCartDrawerEl) {
          cartDrawerEl.classList.toggle('is-empty', newCartDrawerEl.classList.contains('is-empty'));
        }
      }

      if (sections['cart-icon-bubble']) {
        const newDoc = new DOMParser().parseFromString(sections['cart-icon-bubble'], 'text/html');
        const newContent = newDoc.querySelector('.shopify-section');
        const currentBubble = document.getElementById('cart-icon-bubble');
        if (newContent && currentBubble) {
          currentBubble.innerHTML = newContent.innerHTML;
        }
      }

      document.dispatchEvent(new CustomEvent('cart:refresh'));
    }

    openCartDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.classList.remove('is-empty');
        drawer.open();
      }
    }

    escapeHtml(value) {
      const div = document.createElement('div');
      div.textContent = value || '';
      return div.innerHTML;
    }
  }

  customElements.define('category-picks', CategoryPicks);
}
